// The whole sum behind an amount of a confirmed settlement's card, in the
// "i" before it (Lewis, 2026-10-19): "€ 1.000,00 × 10/85 × 245/365 =
// € 78,97". The cost excl. VAT, then every factor that applies: the key's
// share of the party's units, the tenant's or the owner's percentage when
// it is not 100%, and the days.
//
// The amount is the engine's own figure, never this product: the engine
// splits in whole cents over every party, so a party's cent can differ from
// the product rounded on its own. Within that cent the sum reads "≈ € 78,96"
// with the real amount; further off, a factor is missing here (a departure
// settled up to an earlier day, a unit archived in between, several empty
// units with their own days) and no sum is shown rather than a wrong one.

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { toCents } from "./money.utils";
import { formatMoney } from "./moneyText.utils";
import { formatWeight } from "./numberInput.utils";

// One factor of the sum: as it reads, and its value.
export interface ICalculationFactor {
  text: string;
  value: number;
}

// The furthest the engine's split in whole cents puts a party's amount from
// its product rounded on its own.
const ROUNDING_CENTS = 1;

// "10/85", a key's share (a weight can have decimals: "12,5/100").
export const ratioFactor = (
  part: number,
  total: number,
): ICalculationFactor | undefined =>
  total > 0
    ? {
        text: `${formatWeight(part)}/${formatWeight(total)}`,
        value: part / total,
      }
    : undefined;

// "90%", left out at 100%.
export const pctFactor = (pct?: number): ICalculationFactor | undefined =>
  pct == null || pct === 100
    ? undefined
    : {
        text: getLocalizedText("settlement.summary.card.split_pct", {
          pct: formatWeight(pct),
        }),
        value: pct / 100,
      };

// "245/365", the days of the cost's period.
export const daysFactor = (days?: {
  days: number;
  total: number;
}): ICalculationFactor | undefined =>
  days && days.total > 0
    ? { text: `${days.days}/${days.total}`, value: days.days / days.total }
    : undefined;

// "(500/1.000 × 90/365 + 200/1.000 × 30/365)": a sum of products, one
// term per unit, as an owner's vacancy over several empty units, each with
// its weight and its days; a single term without the brackets. None when a
// term misses a factor.
export const sumFactor = (
  terms: (ICalculationFactor | undefined)[][],
): ICalculationFactor | undefined => {
  const known = terms.map(term =>
    term.filter((factor): factor is ICalculationFactor => factor != null),
  );
  const isComplete = known.every(
    (term, index) => term.length > 0 && term.length === terms[index].length,
  );
  if (known.length === 0 || !isComplete) return undefined;
  const texts = known.map(term => term.map(factor => factor.text).join(" × "));
  return {
    text: texts.length === 1 ? texts[0] : `(${texts.join(" + ")})`,
    value: known.reduce(
      (sum, term) =>
        sum + term.reduce((product, factor) => product * factor.value, 1),
      0,
    ),
  };
};

export type TCalculationMatch = "exact" | "rounded";

// "€ 1.000,00 × 10/85 × 245/365 = € 78,97", with whether it is the exact
// product or the engine's amount within a cent of it ("≈"); none without
// a factor or when the product is further off.
export const calculationOf = (params: {
  // The cost excl. VAT.
  base: number;
  factors: (ICalculationFactor | undefined)[];
  // The party's amount excl. VAT, as the row shows it.
  amount: number;
}): { text: string; match: TCalculationMatch } | undefined => {
  const factors = params.factors.filter(
    (factor): factor is ICalculationFactor => factor != null,
  );
  if (factors.length === 0) return undefined;
  const productCents = Math.round(
    factors.reduce((product, factor) => product * factor.value, params.base) *
      100,
  );
  const offCents = Math.abs(productCents - toCents(params.amount));
  if (offCents > ROUNDING_CENTS) return undefined;
  const match: TCalculationMatch = offCents === 0 ? "exact" : "rounded";
  const sum = [
    formatMoney(params.base),
    ...factors.map(factor => factor.text),
  ].join(" × ");
  const sign = match === "exact" ? "=" : "≈";
  return { text: `${sum} ${sign} ${formatMoney(params.amount)}`, match };
};
